// inspired by https://www.30secondsofcode.org/react/s/event-listener-hooks/

import {useEffect, useRef} from "react";

/**
 * Custom hook to add an event listener to a specified element, window, or Promise.
 * The event listener will be removed when the component unmounts or when the dependencies change.
 * @param type - The type of the event to listen for (e.g., "click", "resize").
 * @param handler - The function to call when the event is triggered.
 * @param el - The element to attach the event listener to.
 * This can be a Window, HTMLElement, Promise that resolves to an HTMLElement or PermissionStatus, or null (default is window).
 * @returns A cleanup function that removes the event listener.
 */
type EventHandler<TEvent> = (event: TEvent) => void;

// Generic over the event type so callers listening for a CustomEvent keep
// their own payload type; the DOM only ever hands us an Event, so the cast in
// `listener` is where that caller-supplied shape is taken on trust.
export const useEventListener = <TEvent = Event>(
  type: string,
  handler: EventHandler<TEvent>,
  el:
    | Window
    | HTMLElement
    | Promise<HTMLElement | PermissionStatus>
    | null = window,
) => {
  // Use a ref to store the handler function so that it doesn't change on every render.
  const savedHandler = useRef<EventHandler<TEvent> | undefined>(undefined);

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!el) return;

    // The listener function that will be called when the event is triggered.
    const listener = (e: Event) => savedHandler.current?.(e as TEvent);

    // Attach the event listener to the target element.
    const attachListener = (target: EventTarget | null | undefined) => {
      if (target?.addEventListener) {
        target.addEventListener(type, listener);

        // Return a cleanup function to remove the event listener
        // when the component unmounts or the dependencies change.
        // This ensures that the event listener is removed properly.
        return () => target.removeEventListener(type, listener);
      }
    };

    // If the element is a Promise, resolve it and attach the listener.
    if (el instanceof Promise) {
      el.then(attachListener);

      // If the resolved element has a removeEventListener method, remove the listener.
      return () => {
        el.then((resolved) => {
          if (resolved?.removeEventListener) {
            resolved.removeEventListener(type, listener);
          }
        });
      };
    } else {
      return attachListener(el);
    }
  }, [type, el]);
};
